import React, { useCallback, useEffect, useState } from 'react';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { IconCheck, IconCancel } from '@tabler/icons-react';
import { Controller, useForm } from 'react-hook-form';
import { UpdateCommentSchema, UpdateCommentSchemaType } from '../../../services/housekeeping/schema/UpdateCommentSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import useShowHousekeeping from '../../../services/housekeeping/hooks/useShowHousekeeping';
import { useUpdateComment } from '../../../services/housekeeping/hooks/useUpdateComment';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../../auth/context/AuthProvider';

interface EditableTextWithTooltipProps {
    id: string;
    initialText: string;
}

const EditableTextWithTooltip: React.FC<EditableTextWithTooltipProps> = ({ id, initialText }) => {
    const [isEditing, setIsEditing] = useState(false);
    const { user } = useAuth();

    const { data: housekeeping, isFetching, isSuccess } = useShowHousekeeping(id);

    const { control, handleSubmit, reset } = useForm<UpdateCommentSchemaType>({
        resolver: zodResolver(UpdateCommentSchema),
        defaultValues: {
            comment: '',
        }
    });

    useEffect(() => {
        if (isSuccess && housekeeping) {
            reset({
                comment: housekeeping.comment,
            })
        }
    }, [isSuccess, housekeeping]);

    const { mutate, isPending } = useUpdateComment();
    const queryClient = useQueryClient();

    const onSubmit = useCallback((values: UpdateCommentSchemaType) => {
        toast.loading('Updating room condition...', { id: 'update-room-condition' });

        mutate({
            housekeeping_id: id, data: { comment: values.comment, user_id: user?.id }
        }, {
            onSuccess: async () => {
                // hide overlay trigger
                document.body.click();

                await queryClient.refetchQueries();
                toast.success('Room condition updated successfully', { id: 'update-room-condition' });
            },
            onError: () => {
                toast.error('Failed to update room condition', { id: 'update-room-condition' });
            },
        });
    }, [mutate]);

    const handleCancel = () => {
        setIsEditing(false);
        reset({
            comment: '',
        });
    };

    const renderTooltip = (props: any) => (
        <Tooltip id="tooltip-edit-text" {...props} className="p-3">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="d-flex flex-column">
                    <Controller
                        name="comment"
                        control={control}
                        render={({ field }) => (
                            <textarea
                                {...field}
                                className="form-control"
                                rows={3}
                                placeholder="Enter your comment here..."
                                disabled={isFetching || isPending}
                            />
                        )}
                    />
                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type='button'
                            className="btn btn-sm btn-outline-secondary p-1"
                            onClick={handleCancel}
                        >
                            <IconCancel size={12} />
                        </button>
                        <button
                            type="submit"
                            className="btn btn-sm btn-outline-success p-1"
                        >
                            <IconCheck size={12} />
                        </button>
                    </div>
                </div>
            </form>
        </Tooltip>
    );

    return (
        <OverlayTrigger
            placement="top"
            overlay={renderTooltip}
            show={isEditing}
            onToggle={(nextShow) => setIsEditing(nextShow)}
            trigger={['click']}
        >
            <div className="text-end" style={{ cursor: 'pointer' }}>
                <span
                    style={{
                        textDecoration: 'underline',
                        textDecorationStyle: 'dashed',
                        color: initialText === '' ? 'red' : 'black',
                    }}
                >
                    {initialText === '' ? 'EMPTY' : initialText}
                </span>
            </div>
        </OverlayTrigger>
    );
};

export default EditableTextWithTooltip;
